import React, { useState, useEffect } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import { useToast } from '../../context/ToastContext';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import api from '../../config/api';
import EmptyState from '../common/EmptyState';

const CountryMappingTab = () => {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  const toast = useToast();
  const [mappings, setMappings] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [formData, setFormData] = useState({
    country_code: '',
    country_name: '',
    mapped_region: ''
  });

  const REGION_OPTIONS = ['US', 'CA', 'AU', 'UK', 'EU', 'INT'];

  useEffect(() => {
    fetchMappings();
    fetchAvailableCountries();
  }, []);

  const fetchMappings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/financial-config/country-mappings');

      if (response.data.success) {
        setMappings(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching country mappings:', err);
      toast.error(err.response?.data?.error || 'Failed to load country mappings');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCountries = async () => {
    try {
      const response = await api.get('/api/financial-config/country-mappings/available-countries');

      if (response.data.success) {
        setAvailableCountries(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching available countries:', err);
    }
  };

  const handleOpenDialog = (mapping = null) => {
    if (mapping) {
      setEditingMapping(mapping);
      setFormData({
        country_code: mapping.country_code,
        country_name: mapping.country_name,
        mapped_region: mapping.mapped_region
      });
    } else {
      setEditingMapping(null);
      setFormData({
        country_code: '',
        country_name: '',
        mapped_region: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMapping(null);
    setFormData({
      country_code: '',
      country_name: '',
      mapped_region: ''
    });
  };

  const handleSave = async () => {
    try {
      if (!formData.country_code || !formData.country_name || !formData.mapped_region) {
        toast.error('All fields are required');
        return;
      }

      if (editingMapping) {
        // Update existing mapping
        await api.put(`/api/financial-config/country-mappings/${editingMapping.id}`, formData);
        toast.success('Country mapping updated successfully');
      } else {
        // Create new mapping
        await api.post('/api/financial-config/country-mappings', formData);
        toast.success('Country mapping created successfully');
      }

      handleCloseDialog();
      fetchMappings();
    } catch (err) {
      console.error('Error saving country mapping:', err);
      toast.error(err.response?.data?.error || 'Failed to save country mapping');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Mapping',
      message: 'Are you sure you want to delete this country mapping?',
      confirmText: 'Delete',
      severity: 'danger',
    });
    if (!confirmed) return;

    try {
      await api.delete(`/api/financial-config/country-mappings/${id}`);
      toast.success('Country mapping deleted successfully');
      fetchMappings();
    } catch (err) {
      console.error('Error deleting country mapping:', err);
      toast.error(err.response?.data?.error || 'Failed to delete country mapping');
    }
  };

  const handleCountrySelect = (countryValue) => {
    const selected = availableCountries.find(c => c.country_value === countryValue);
    if (selected) {
      setFormData({
        ...formData,
        country_code: selected.country_value,
        country_name: selected.country_label
      });
    }
  };

  const getRegionColor = (region) => {
    const colors = {
      'US': 'primary',
      'CA': 'secondary',
      'AU': 'success',
      'UK': 'info',
      'EU': 'warning',
      'INT': 'default'
    };
    return colors[region] || 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PublicIcon />
            Country / Region Mapping
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define how countries from your advertising data are grouped into regions for financial reporting
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Mapping
        </Button>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!loading && (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell><strong>Country Code</strong></TableCell>
                <TableCell><strong>Country Name</strong></TableCell>
                <TableCell><strong>Mapped Region</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, border: 0 }}>
                    <EmptyState
                      compact
                      icon={PublicIcon}
                      title="No country mappings configured"
                      description="Click 'Add Mapping' to create your first mapping"
                      actionLabel="Add Mapping"
                      actionIcon={<AddIcon />}
                      onAction={() => handleOpenDialog()}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                mappings.map((mapping) => (
                  <TableRow key={mapping.id} hover>
                    <TableCell>
                      <Chip label={mapping.country_code} size="small" />
                    </TableCell>
                    <TableCell>{mapping.country_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={mapping.mapped_region}
                        color={getRegionColor(mapping.mapped_region)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(mapping)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(mapping.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMapping ? 'Edit Country Mapping' : 'Add Country Mapping'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {!editingMapping && availableCountries.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Select Country</InputLabel>
                <Select
                  value={formData.country_code}
                  onChange={(e) => handleCountrySelect(e.target.value)}
                  label="Select Country"
                >
                  {availableCountries.map((country) => (
                    <MenuItem key={country.country_value} value={country.country_value}>
                      {country.country_label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Country Code"
              value={formData.country_code}
              onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
              fullWidth
              required
              disabled={editingMapping !== null}
              helperText="The exact country code as it appears in your ads data"
            />

            <TextField
              label="Country Name"
              value={formData.country_name}
              onChange={(e) => setFormData({ ...formData, country_name: e.target.value })}
              fullWidth
              required
              helperText="Human-readable country name"
            />

            <FormControl fullWidth required>
              <InputLabel>Mapped Region</InputLabel>
              <Select
                value={formData.mapped_region}
                onChange={(e) => setFormData({ ...formData, mapped_region: e.target.value })}
                label="Mapped Region"
              >
                {REGION_OPTIONS.map((region) => (
                  <MenuItem key={region} value={region}>
                    <Chip
                      label={region}
                      color={getRegionColor(region)}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {region === 'US' && 'United States'}
                    {region === 'CA' && 'Canada'}
                    {region === 'AU' && 'Australia'}
                    {region === 'UK' && 'United Kingdom'}
                    {region === 'EU' && 'European Union'}
                    {region === 'INT' && 'International (Others)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingMapping ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
};

export default CountryMappingTab;
